import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {UIArtefaktTyp} from '../../models/uiArtefaktTyp';
import {ErrorService, inOutAnimation, ISymbol} from '@lv1871/core';
import {firstNestedSymbolControl, flatMap, ListControl, ListItemControl, UIArtefakt, UiBaseControl} from '../../models/uiArtefakt';
import {UIAusrichtung} from '../../models/uiAusrichtung';
import {ShellConfigService} from '../../services/shell-config.service';
import {UiUpdateService} from '../../services/ui-update.service';
import {ControlContainer, NgForm} from '@angular/forms';
import {ValidationService} from '../../services/validation.service';
import {v4 as uuid} from 'uuid';
import {EventService} from '../../services/event.service';
import {interval, Subject, Subscription} from 'rxjs';
import {filter, takeWhile, tap} from 'rxjs/operators';
import {MeldungPipe} from '../../pipes/meldung.pipe';

@Component({
    selector: 'lv-liste',
    templateUrl: './liste.component.html',
    animations: [inOutAnimation],
    providers: [MeldungPipe],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    styleUrls: ['./liste.component.scss'],
    standalone: false
})
export class ListeComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(NgForm, {static: true})
  public ngForm: NgForm;

  public uiAusrichtung: UIAusrichtung;
  public showUntruncatedError: boolean = false;
  public nestedSymbol: ISymbol;
  public showInfotextCollapsable: boolean = false;
  public initalTemplateUsed: boolean = false;
  public templateUsed: boolean = false;
  public uiControlsRequestPending: boolean;
  public showSpinner: boolean;

  private _uiControlsRequestPendingSubscription$ = Subscription.EMPTY;
  private _hinzufuegenClickedSubscription$ = Subscription.EMPTY;
  private _hinzufuegenClicked$ = new Subject<boolean>();
  private _uiArtefakt: ListControl;

  constructor(private _shellConfigService: ShellConfigService, private _errorService: ErrorService,
              private _eventService: EventService, private _uiUpdateService: UiUpdateService,
              private _meldungPipe: MeldungPipe,
              private _validationService: ValidationService,
              private _renderer: Renderer2, private _element: ElementRef) {
  }

  get uiArtefakt(): ListControl {
    return this._uiArtefakt;
  }

  public get hiddenInputmodel(): number {
    return this._uiArtefakt.UIArtefakt.length;
  }

  @Input() set uiArtefakt(uiArtefakt: ListControl) {
    if (uiArtefakt.ListeErweiterbarJN) {
      const newListControl = this._extractListItemTemplate(uiArtefakt);
      this._deriveUniqueTrackingIDs(this._uiArtefakt, newListControl);
      this._uiArtefakt = newListControl;
    } else {
      this._uiArtefakt = uiArtefakt;
    }
    this._validateArtefakte();
  }

  public set hiddenInputmodel(value: number) {
  }

  ngAfterViewInit(): void {
    this._renderer.setAttribute(this._element.nativeElement, 'cypress-uuid', this.uiArtefakt.IdentifikatorUuid);
    if(!!this.uiArtefakt.ReferenzIdentifikator) {
      this._renderer.setAttribute(this._element.nativeElement, 'cypress-refId', this.uiArtefakt.ReferenzIdentifikator);
    }
  }

  public ngOnInit(): void {
    this._uiControlsRequestPendingSubscription$ = this._eventService.uiControlsRequestPending$.pipe(
        tap(requestPending => this.uiControlsRequestPending = requestPending),
        filter(requestPending => !requestPending),
        tap(_ => this.showSpinner = false)
    ).subscribe();

    this._hinzufuegenClickedSubscription$ = this._hinzufuegenClicked$.pipe(
        tap(_ => this._uiUpdateService.valueChangedSubject$.next(this._uiArtefakt)),
    ).subscribe();

    this.uiAusrichtung = this._shellConfigService.getParameter().uiAusrichtung;
    this.nestedSymbol = firstNestedSymbolControl(this.uiArtefakt.UIArtefakt);
    if (this._uiArtefakt.ListeErweiterbarJN) {
      if (this._uiArtefakt.UIArtefakt.length === 0 && this._uiArtefakt.ListenelementeMin > 0) {
        const newentry = this._newEntryFromTemplate();
        newentry.UIArtefakt.forEach(artef => (artef as UiBaseControl).ReferenzListItemKey = newentry.UniqueTrackingID);
        this._uiArtefakt.UIArtefakt.push(newentry);
        this.initalTemplateUsed = true;
        this.templateUsed = true;
      }
      this._validationService.registerForm(this.uiArtefakt, this.ngForm);
      this.ngForm.control.updateValueAndValidity();
    }
  }

  public ngOnDestroy(): void {
    if (this._hinzufuegenClickedSubscription$) {
      this._hinzufuegenClickedSubscription$.unsubscribe();
    }
    if (this._uiControlsRequestPendingSubscription$) {
      this._uiControlsRequestPendingSubscription$.unsubscribe();
    }
  }

  public infotextCollapsableChange(event) {
    this.showInfotextCollapsable = event;
  }

  public addEntryFromTemplate() {
    if (!this.hasReachedMax() && !this.showSpinner) {
      this.showSpinner = true;
      interval(100).pipe(
          tap(_ => this._uiArtefakt.UIArtefakt.push(this._newEntryFromTemplate())),
          filter(_ => !this.uiControlsRequestPending),
          tap(_ => this._hinzufuegenClicked$.next(true)),
          takeWhile(() => this.uiControlsRequestPending)).subscribe();
    }
  }

  public removeEntry(index: number) {
    if (!this.hasReachedMin()) {
      this._uiArtefakt.UIArtefakt.splice(index, 1);
      this._uiUpdateService.valueChangedSubject$.next(this._uiArtefakt);
    }
  }

  public trackByFn(index: number, uiArtefakt: UIArtefakt) {
    return (uiArtefakt as ListItemControl).UniqueTrackingID;
  }

  public hasReachedMin() {
    return (!!this._uiArtefakt.ListenelementeMin && this._uiArtefakt.UIArtefakt.length <= this._uiArtefakt.ListenelementeMin);
  }

  public hasReachedMax() {
    return (!!this._uiArtefakt.ListenelementeMax && this._uiArtefakt.UIArtefakt.length >= this._uiArtefakt.ListenelementeMax);
  }

  public minEqualsMax() {
    return (this._uiArtefakt.ListenelementeMin === this._uiArtefakt.ListenelementeMax);
  }

  public isInvalid() {
    return this.ngForm?.touched && !!this.getError();
  }

  public onErrorClick() {
    this.showUntruncatedError = !this.showUntruncatedError;
  }

  public getError() {
      if (this.uiArtefakt.UIMeldung?.length) {
        return this._meldungPipe.getMostImportantMeldungstext(this.uiArtefakt.UIMeldung);
      }
      if (!!this._uiArtefakt.ListenelementeMin && this._uiArtefakt.UIArtefakt.length < this._uiArtefakt.ListenelementeMin) {
        if (this._uiArtefakt.ListenelementeMin === 1) {
          return 'Es muss mindestens ein Eintrag angelegt werden.';
        } else {
          return 'Es müssen mindestens ' + this._uiArtefakt.ListenelementeMin + ' Einträge angelegt werden.';
        }
      } else if (!!this._uiArtefakt.ListenelementeMax && this._uiArtefakt.UIArtefakt.length > this._uiArtefakt.ListenelementeMax) {
        if (this._uiArtefakt.ListenelementeMax === 1) {
          return 'Es darf höchstens ein Eintrag angelegt werden.';
        } else {
          return 'Es dürfen höchstens ' + this._uiArtefakt.ListenelementeMax + ' Einträge angelegt werden.';
        }
      }

    this.showUntruncatedError = false;
    return null;
  }

  private _extractListItemTemplate(listControl: ListControl): ListControl {
    const templateList = listControl.UIArtefakt?.filter(artefakt => (artefakt as ListItemControl).TemplateJN);
    if (templateList.length === 0 && this.templateUsed) {
      this._errorService.pushErrorMessage(`Die erweiterbare Liste ${listControl.Identifikator} enthält kein Template!`);
    } else if (templateList.length === 0 && !this.templateUsed) {
      if (!!this._uiArtefakt) {
        listControl.template = this._uiArtefakt.template;
      }
    } else {
      listControl.template = templateList[0] as ListItemControl;
      listControl.UIArtefakt = listControl.UIArtefakt.filter(artefakt => !(artefakt as ListItemControl).TemplateJN);
      this.initalTemplateUsed = false;
      this.templateUsed = false;
    }
    return listControl;
  }

  private _newEntryFromTemplate(): ListItemControl {
    const templateCopy = JSON.parse(JSON.stringify(this._uiArtefakt.template)) as ListItemControl;
    templateCopy.TemplateJN = false;
    templateCopy.UniqueTrackingID = this._generateUniqueTrackingID();
    this.templateUsed = true;
    return templateCopy;
  }

  private _deriveUniqueTrackingIDs(oldControl: ListControl, newControl: ListControl) {
    if (!oldControl || !oldControl.UIArtefakt) {
      newControl.UIArtefakt.forEach(item => (item as ListItemControl).UniqueTrackingID = this._generateUniqueTrackingID());
    } else {
      let lastMatchIndex = -1;
      let firstNewEntryIndex = oldControl.UIArtefakt.length;
      for (let i = 0; i < oldControl.UIArtefakt.length; i++) {
        if (oldControl.UIArtefakt[i].Identifikator.indexOf('@') < 0) {
          firstNewEntryIndex = i;
          break;
        }
        const matchIndex = newControl.UIArtefakt.findIndex(artefakt => artefakt.Identifikator === oldControl.UIArtefakt[i].Identifikator);
        if (matchIndex > lastMatchIndex) {
          lastMatchIndex = matchIndex;
        }
      }
      let newIndex = 0;
      newControl.UIArtefakt.forEach(item => {
        const oldIndex = oldControl.UIArtefakt.findIndex(artefakt => artefakt.Identifikator === item.Identifikator);
        if (oldIndex >= 0) {
          // match by Identifikator
          (item as ListItemControl).UniqueTrackingID = (oldControl.UIArtefakt[oldIndex] as ListItemControl).UniqueTrackingID;
        } else if (newIndex > lastMatchIndex && newIndex - lastMatchIndex <= oldControl.UIArtefakt.length - firstNewEntryIndex) {
          // match by index of new entry
          (item as ListItemControl).UniqueTrackingID =
              (oldControl.UIArtefakt[firstNewEntryIndex + newIndex - lastMatchIndex - 1] as ListItemControl).UniqueTrackingID;
        } else {
          // no match
          (item as ListItemControl).UniqueTrackingID = this._generateUniqueTrackingID();
        }
        newIndex++;
      });
    }
  }

  private _generateUniqueTrackingID(): string {
    return uuid();
  }

  private _validateArtefakte() {
    this._uiArtefakt.UIArtefakt?.forEach(artefakt => {
      if (artefakt.UIArtefakttyp !== UIArtefaktTyp.LISTENELEMENT) {
        this._errorService.pushErrorMessage('In der Liste ' + this._uiArtefakt.Identifikator
            + ' ist ein Artefakt, das kein Listenelement ist: ' + artefakt.Identifikator);
      } else {
        flatMap(artefakt.UIArtefakt).filter(artef => artef.UIArtefakttyp !== 'UIVorgangReferenz' && !!artef['UIVorgangID'])
        .forEach(artef => {
          this._errorService.pushErrorMessage('In der Liste ' + this._uiArtefakt.Identifikator
              + ' ist ein Artefakt enthalten, für das eine UIVorgangID gesetzt ist: ' + artef.Identifikator + ' (UIVorgangID: ' + artef['UIVorgangID']
              + '). Dies ist ein Modellierungsfehler, da die UIVorgangID (in einer Liste) nicht eindeutig einem Vorgang zugeordnet werden kann.');
        });
      }
    });
  }
}
